/**
 * Passkey environment helpers (frontend mirror of the backend's
 * modules/authentication/utils/rp.ts).
 *
 * Passkeys are bound to a Relying Party ID = the site's host with any leading
 * "www." stripped (port excluded). The practical consequences users keep tripping
 * over:
 *   - A passkey created on `localhost:3000` only works in local development.
 *   - A passkey created on `tommillerservices.com` only works on the live site.
 *   - They are NOT interchangeable — `localhost` ≠ a production domain.
 *   - apex and www ARE the same site (`tommillerservices.com` == `www.tommillerservices.com`).
 *
 * These helpers let the /security page label each passkey "works here" vs
 * "other site", and let the login modal warn that only same-site passkeys appear.
 */

export type PasskeyScope = "this-site" | "other-site" | "unknown";

export interface PasskeyClassification {
    /** True when this passkey was created on (an equivalent of) the current site. */
    worksHere: boolean;
    scope: PasskeyScope;
    /** Short badge label, e.g. "Works on this site" / "Other site — tommillerservices.com". */
    label: string;
    /** Longer explanation for a tooltip. */
    detail: string;
}

/** Lower-case a host and drop a single leading "www." so apex/www collapse to one identity. */
const stripWww = (host: string): string => host.replace(/^www\./i, "").toLowerCase();

/** The current site's rpID (hostname, www-stripped, no port). "" when not in a browser. */
export function currentRpId(hostname?: string): string {
    const h = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
    return h ? stripWww(h) : "";
}

/** The current site's host incl. port, e.g. "localhost:3000". */
export function currentHost(host?: string): string {
    return host ?? (typeof window !== "undefined" ? window.location.host : "");
}

/** Are we running against localhost (local development)? */
export function isLocalhost(hostname?: string): boolean {
    const h = currentRpId(hostname);
    return h === "localhost" || h === "127.0.0.1" || h === "::1";
}

/**
 * Classify a passkey relative to the site the user is currently on.
 * `pk.rpId` / `pk.host` are recorded by the backend at registration (added 2026-06-16).
 *
 * @param pk        the passkey ({ rpId, host } — extra fields ignored).
 * @param hostname  override the current hostname (tests pass this; prod reads window).
 */
export function classifyPasskeyForCurrentSite(
    pk: { rpId?: string | null; host?: string | null },
    hostname?: string
): PasskeyClassification {
    const here = currentRpId(hostname);
    const cred = pk.rpId ? stripWww(pk.rpId) : "";

    if (!cred) {
        return {
            worksHere: false,
            scope: "unknown",
            label: "Unknown site",
            detail:
                "This passkey was created before we started recording which site it belongs to. " +
                "If it doesn't work when you try to sign in, remove it and add a fresh one here.",
        };
    }

    if (cred === here) {
        return {
            worksHere: true,
            scope: "this-site",
            label: "Works on this site",
            detail: `Created on ${pk.host || cred} — the site you're on right now, so you can sign in with it here.`,
        };
    }

    const isLocalCred = cred === "localhost" || cred === "127.0.0.1" || cred === "::1";
    return {
        worksHere: false,
        scope: "other-site",
        label: `Other site — ${cred}`,
        detail:
            `This passkey is for ${pk.host || cred}, not this site (${currentHost(hostname ? hostname : undefined) || here}). ` +
            `It won't appear or work here. ` +
            (isLocalCred
                ? "It's a local-development passkey — it only works on localhost."
                : "It only works on its own domain."),
    };
}
